import AddCharacterModal from '../../TeamRoster/components/AddCharacterModal'
import { useMutation } from '@tanstack/react-query'
import { createEventParticipant } from 'app/frontend/base/api/event'
import { useTeam } from 'app/frontend/base/config/context/team'
import useApplyEventUpdate from '../hooks/useApplyEventUpdate'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useMemo } from 'react'

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  event: wowaudit.Event
}

const AddParticipantModal = ({ open, setOpen, event }: Props) => {
  const { team } = useTeam()
  const applyEventUpdate = useApplyEventUpdate()
  const { t } = useTranslation()

  const existingCharacterIds = useMemo(() => {
    return new Set(
      event.participants.map((participant) => participant.characterId),
    )
  }, [event.participants])

  const existingUserIds = useMemo(() => {
    return new Set(
      event.participants
        .map((participant) => participant.characterReference.userId)
        .filter((userId) => userId !== null),
    )
  }, [event.participants])

  const addParticipantMutation = useMutation({
    mutationFn: async (params: {
      characterId: number
      specId?: number
      role?: string
    }) =>
      createEventParticipant(team.id, {
        eventId: event.id,
        characterId: params.characterId,
        addedAsGuest: true,
        role: params.role,
        specId: params.specId,
      }),
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
      toast.success(t('dashboard.team.roster.member-added'))
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  return (
    <AddCharacterModal
      open={open}
      setOpen={setOpen}
      mode='event'
      title='Add participants'
      defaultTab='roster'
      hideTrackingRulesTab
      existingCharacterIds={existingCharacterIds}
      existingUserIds={existingUserIds}
      onAddCharacter={(params) => addParticipantMutation.mutate(params)}
      isAddingAny={addParticipantMutation.isPending}
      addingCharacterId={addParticipantMutation.variables?.characterId}
    />
  )
}

export default AddParticipantModal
